import EditModal from '../../../../pages/expenses/edit-modal';
import ConfirmationModal from '../../../../components/shared/confirmation-modal';
import Loader from '../../../../components/shared/loader';
import Modal from '../../../../components/shared/modal';
import Pagination from '../../../../components/shared/pagination';
import UpdateStatusModal from '../../../../components/shared/update-status-modal';
import { deleteExpense, getAllExpensesStatuses, updateExpenseStatus } from '../../../../services/expenses-service';
import { formatUTCTimestampToLocal } from '../../../../utils/set-date-range-utils';
import { statusColors } from '../../../../utils/status-color-utils';
import { camelCaseToWords } from '../../../../utils/structure-words-utils';
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react'
import { FiMoreVertical } from 'react-icons/fi';
import { RiArrowUpSLine } from 'react-icons/ri';
import { RxCaretSort } from 'react-icons/rx';

const Expenses = ({ data, loading, error, setData, globalFilter,attributes, onGlobalFilterChange, accountId, fetchExpenses, newlyAddedItem, setNewlyAddedItem, jobsOptions, spendors, vendors, categories, paymentMethods }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sorting, setSorting] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [statusError, setStatusError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        const fetchGlobalData = async () => {
            if (!accountId) return;
            setLoadingStatus(true);

            try {
                const [statusRes] = await Promise.all([
                    getAllExpensesStatuses(accountId),
                ]);
                if (statusRes.success) setStatusOptions(statusRes.data.data || []);
            } catch (err) {
                console.error("Failed to fetch global data", err);
                setStatusError('Failed to fetch global data');
            } finally {
                setLoadingStatus(false);
            }
        };

        fetchGlobalData();
    }, [accountId]);


    useEffect(() => {
        if (newlyAddedItem) {
            setSelectedRow(newlyAddedItem);
            setIsEditModalOpen(true);
            setNewlyAddedItem(null);
        }
    }, [newlyAddedItem, setNewlyAddedItem]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleActionClick = (id) => {
        setOpenMenuId(openMenuId === id ? null : id);
    };

    const closeMenu = () => {
        setOpenMenuId(null);
    };

    const handleRowClick = (row, e) => {
        if (!e.target.closest('input[type="checkbox"]')) {
            setSelectedRow(row.original);
            setIsEditModalOpen(true);
        }
    };

    const handleConfirmAction = (action, row) => {
        setSelectedRow(row.original);
        if (action === 'Change Status') {
            setIsUpdateStatusModalOpen(true);
        } else {
            setConfirmAction(action);
            setIsConfirmModalOpen(true);
        }
        setErrorMessage(null);
        closeMenu();
    };


    const handleConfirm = async () => {
        if (!selectedRow || !confirmAction) return;

        if (confirmAction === 'delete') {
            try {
                setIsDeleting(true);
                const expenseId = selectedRow.expenseId;
                const res = await deleteExpense(accountId, expenseId);

                if (res.status === 204) {
                    setData(prevData => prevData.filter(item => item.expenseId !== expenseId));
                    setIsConfirmModalOpen(false);
                    setSelectedRow(null);
                    setConfirmAction(null);
                    setSuccessMessage('Expense deleted successfully!');
                } else {
                    setErrorMessage(res.data?.message || 'Failed to delete Expense entry');
                }
            } catch (error) {
                console.error('Error deleting Expense:', error);
                setErrorMessage('An error occurred while deleting the Expense entry');
            } finally {
                setIsDeleting(false);
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = () => closeMenu();
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Define columns with sorting configuration
    const columns = useMemo(
        () => [
            {
                id: 'select',
                header: ({ table }) => (
                    <div className="flex justify-center w-full">
                        <input
                            type="checkbox"
                            checked={table.getIsAllRowsSelected()}
                            onChange={(e) => {
                                e.stopPropagation();
                                table.getToggleAllRowsSelectedHandler()(e);
                            }}
                            className="w-4 h-4"
                        />
                    </div>
                ),
                cell: ({ row }) => (
                    <div className="flex justify-center w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="checkbox"
                            checked={row.getIsSelected()}
                            onChange={(e) => {
                                e.stopPropagation(); // Stop event bubbling
                                row.getToggleSelectedHandler()(e); // Toggle checkbox
                            }}
                            className="w-4 h-4"
                        />
                    </div>
                ),
                enableSorting: false,
                size: 50,
            },
            {
                accessorKey: 'expenseTime',
                header: 'Time',
                enableSorting: true,
                cell: info => {
                    const value = info.getValue();
                    return value ? formatUTCTimestampToLocal(value, 'yyyy-MM-dd hh:mm a') : '-';
                },
            },
            {
                accessorKey: 'vendor.name',
                header: 'Vendor',
                enableSorting: false,
                cell: info => camelCaseToWords(info.getValue()) || '----',
            },
            {
                accessorKey: 'category',
                header: 'Category',
                enableSorting: false,
                cell: info => camelCaseToWords(info.getValue()) || '----',
            },
            {
                accessorKey: 'currency',
                header: 'Currency',
                enableSorting: false,
                cell: info => info.getValue() || '----',
            },
            {
                accessorKey: 'amount',
                header: `Amount (${attributes?.currency})`,
                // cell: info => `${info.getValue().toFixed(2)}`,
                enableSorting: true,
                cell: info => info.getValue() || '----',
            },
            {
                accessorKey: 'spender.name',
                header: 'Spender',
                enableSorting: true,
                cell: info => camelCaseToWords(info.getValue()) || '----',
            },
            // {
            //     accessorKey: 'vehicle.name',
            //     header: 'Vehicle',
            //     cell: info => camelCaseToWords(info.getValue()) || '----',
            //     enableSorting: false,
            // },
            {
                accessorKey: 'jobId',
                header: 'Job',
                enableSorting: false,
                cell: info => info.getValue() || '----',
            },

            {
                accessorKey: 'status',
                header: 'Status',
                enableSorting: false,
                cell: info => {
                    const status = info.getValue().toLowerCase();
                    const color = statusColors[status] || statusColors.default;
                    const displayStatus = camelCaseToWords(info.getValue());
                    return (
                        <div className="flex justify-start ">
                            <span
                                className=" py-0.5 rounded-md text-sm font-medium capitalize text-center whitespace-nowrap"
                                style={{
                                    backgroundColor: color,
                                    color: 'black',
                                    border: `1px solid ${color}`,
                                    width: '100px',
                                    display: 'inline-block',
                                    textAlign: 'center'
                                }}
                            >
                                {displayStatus}
                            </span>
                        </div>
                    );
                },
            },

            {
                id: 'actions',
                header: 'Action',
                cell: ({ row }) => {
                    const rowId = row.id;

                    return (
                        <div className="relative flex justify-center w-full">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleActionClick(rowId);
                                }}
                                className="p-1 rounded-full hover:bg-gray-200"
                            >
                                <FiMoreVertical className="text-gray-600 text-sm" />
                            </button>
                            {openMenuId === rowId && (
                                <div
                                    className={`absolute right-0 w-32 bg-white rounded-md shadow-md z-10 border border-gray-200 top-5`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        className="block w-full text-left px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                                        onClick={() => handleConfirmAction('Change Status', row)}
                                    >
                                        Change Status
                                    </button>
                                    <button
                                        className="block w-full text-left px-4 py-1.5 text-sm text-red-600 hover:bg-gray-100"
                                        onClick={() => handleConfirmAction('delete', row)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                },
                enableSorting: false,
                size: 50,
            },
        ],
        [openMenuId]
    );

    // Initialize the table
    const table = useReactTable({
        data, // Use the full dataset - no pre-filtering needed
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
        enableSortingRemoval: false,
        // Add global filter function
        globalFilterFn: (row, columnId, filterValue) => {
            const searchLower = filterValue.toLowerCase();
            const rowValue = String(row.getValue(columnId)).toLowerCase();

            if (columnId === 'expenseTime') {
                return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
            } else if (columnId.includes('.')) {
                const [parent, child] = columnId.split('.');
                const value = row.original[parent]?.[child] || '';
                return String(value).toLowerCase().includes(searchLower);
            }

            return rowValue.includes(searchLower);
        },
    });

    return (
        <div className='w-full'>
            {successMessage && (
                <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
                    {successMessage}
                </div>
            )}
            <div className="overflow-x-auto max-w-full pb-16">
                {
                    (loading || loadingStatus) ? <Loader />
                        :
                        <table className="min-w-full text-sm border border-gray-200">
                            <colgroup>
                                <col style={{ width: '50px' }} />
                                <col />
                                <col />
                                <col />
                                <col />
                                <col />
                                <col />
                                <col />
                                <col />
                                {/* <col /> */}
                                <col style={{ width: '50px' }} />
                            </colgroup>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                className="px-2 py-1.5 border-b capitalize"
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                    width: header.column.getSize(),
                                                }}
                                            >
                                                <div className="flex items-center gap-1">
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {header.column.getCanSort() && (
                                                        <span className="text-black">
                                                            {{
                                                                asc: <RiArrowUpSLine className="text-base mt-0.5" />,
                                                                desc: <RiArrowUpSLine className="text-base rotate-180" />,
                                                            }[header.column.getIsSorted()] ?? (
                                                                    <RxCaretSort className="text-lg" />
                                                                )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows
                                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                    .map(row => (
                                        <tr
                                            key={row.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={(e) => handleRowClick(row, e)}
                                        >
                                            {row.getVisibleCells().map(cell => (
                                                <td
                                                    key={cell.id}
                                                    className="px-2 py-1 border-b whitespace-nowrap"
                                                    style={{ width: cell.column.getSize() }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                }
            </div>

            <Modal
                isOpen={isEditModalOpen && selectedRow}
                onClose={() => setIsEditModalOpen(false)}
                modalClass={'xl:w-[40%] lg:w-[45%] md:w-[55%] sm:w-[70%] w-[90%] '}
            >
                <EditModal
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    setIsEditModalOpen={setIsEditModalOpen}
                    isVehicleTab={true}
                    isVehicleEditable={false}
                    spendors={spendors}
                    vendors={vendors}
                    showVehicle={false}
                    setData={setData}
                    jobsOptions={jobsOptions}
                    categories={categories}
                    paymentMethods={paymentMethods}
                />
            </Modal>

            <Modal
                isOpen={isUpdateStatusModalOpen && selectedRow}
                onClose={() => {
                    setIsUpdateStatusModalOpen(false);
                    setSelectedRow(null);
                }}
                modalClass={'xl:!w-[25%] lg:!w-[34%] md:!w-[40%] sm:!w-[45%] !w-[90%] !px-0'}
            >

                <UpdateStatusModal
                    confirmAction={confirmAction}
                    setIsConfirmModalOpen={setIsConfirmModalOpen}
                    handleConfirm={handleConfirm}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    setData={setData}
                    setIsUpdateStatusModalOpen={setIsUpdateStatusModalOpen}
                    fetchData={fetchExpenses}
                    fromVehiclesTab={true}
                    accountId={accountId}
                    modalTitle='Change Expense Status'
                    statusKey='status'
                    statusOptions={statusOptions}
                    updateStatusApi={updateExpenseStatus}
                    uniqueIdKey='expenseId'
                />
            </Modal>

            <Modal
                isOpen={isConfirmModalOpen && selectedRow}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                    setErrorMessage(null);
                    setSelectedRow(null);
                }}
                modalClass={'xl:!w-[28%] lg:!w-[35%] md:w-[40%] sm:w-[60%] w-[90%]'}
            >
                <ConfirmationModal
                    confirmAction={confirmAction}
                    setIsConfirmModalOpen={setIsConfirmModalOpen}
                    handleConfirm={handleConfirm}
                    loading={isDeleting}
                    errorMessage={errorMessage}
                />
            </Modal>

            <Pagination
                currentPage={currentPage}
                totalItems={table.getFilteredRowModel().rows.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(count) => {
                    setItemsPerPage(count);
                    setCurrentPage(1);
                }}
            />
        </div>
    )
}

export default Expenses
